/** Maximum position history requests in flight at the same time on the Trackers page. */
export const MAX_CONCURRENT_TRACKER_REQUESTS = 3;

export interface TrackerRequestLimiter {
    run: <T>(task: () => Promise<T>, signal?: AbortSignal) => Promise<T>;
}

const abortError = () => {
    const error = new Error("canceled");
    error.name = "CanceledError";
    return error;
};

/**
 * Runs at most `maxConcurrent` tasks at once. A queued task whose signal aborts
 * leaves the queue without running. A task gets its slot synchronously when one is free.
 */
export const createTrackerRequestLimiter = (
    maxConcurrent = MAX_CONCURRENT_TRACKER_REQUESTS
): TrackerRequestLimiter => {
    let running = 0;
    const queue: Array<() => void> = [];

    const release = () => {
        running -= 1;
        const next = queue.shift();
        next?.();
    };

    const run = <T,>(task: () => Promise<T>, signal?: AbortSignal): Promise<T> => new Promise<T>((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortError());
            return;
        }

        const start = () => {
            signal?.removeEventListener("abort", onAbort);
            running += 1;
            let pending: Promise<T>;
            try {
                pending = task();
            } catch (error) {
                release();
                reject(error);
                return;
            }
            pending.then(resolve, reject).finally(release);
        };

        const onAbort = () => {
            const index = queue.indexOf(start);
            if (index >= 0) {
                queue.splice(index, 1);
                reject(abortError());
            }
        };

        if (running < maxConcurrent) {
            start();
            return;
        }
        signal?.addEventListener("abort", onAbort);
        queue.push(start);
    });

    return {run};
};
